import { record } from '@rrweb/record';
import type { eventWithTime } from '@rrweb/types';
import { sanitizeEvent } from './ux-event-filter';

/** Opt-in only: elements whose text is (in this repo) always a translated/static label. Data-bearing
 *  elements (buttons with names, nav/menu items, dialog titles, options, chips) are deliberately absent —
 *  mark individual labels with data-ux-keep instead. See design.md § 3. */
export const KEEP_SELECTOR = 'mat-label, mat-error, mat-hint, mat-icon, mat-paginator, [data-ux-keep]';
export const MASK_SELECTOR = '[data-ux-mask]';
export const BLOCK_SELECTOR =
  'canvas, iframe, img, video, svg, input[type=file], .mapboxgl-map, .textLayer, pdf-viewer, .cm-content, [data-ux-block]';

export function keep(el: Element | null): boolean {
  if (!el) return false;
  const kept = el.closest(KEEP_SELECTOR);
  if (!kept) return false;
  const masked = el.closest(MASK_SELECTOR);
  if (!masked) return true;
  // Both apply: the innermost marker wins.
  return !kept.contains(masked);
}

/** The exact option type record() accepts, derived from its own signature (not the transitive
 *  'rrweb' package, which pnpm's strict node_modules doesn't expose) — a real type-check instead
 *  of a hand-maintained shape that can silently drift from what rrweb actually reads.
 *  `record<eventWithTime>` (an instantiation expression, not a call) is load-bearing: `record`'s
 *  own default type parameter (`T = eventWithTime`) is NOT picked up by `Parameters<typeof record>`,
 *  which instead resolves the unconstrained `T` to `unknown` — silently widening every callback's
 *  event parameter and hiding real mismatches. Confirmed only by the AOT build (Vitest doesn't
 *  type-check); do not revert to the plain `typeof record` form. */
type RecordOptions = NonNullable<Parameters<typeof record<eventWithTime>>[0]>;

/** The full record() option set — single source of truth, unit-tested. NOTE: never set maskAllInputs
 *  (true replaces a supplied maskInputOptions with rrweb's fixed map). maskTextSelector:'*' is
 *  load-bearing: without it rrweb never invokes maskTextFn. */
export function buildRecordOptions(emit: (e: eventWithTime) => void): RecordOptions {
  return {
    emit: (e: eventWithTime) => emit(sanitizeEvent(e)),
    maskTextSelector: '*',
    maskTextFn: (text: string, el: HTMLElement | null) => (keep(el) ? text : '*'.repeat(text.length)),
    maskInputOptions: {
      color: true,
      date: true,
      'datetime-local': true,
      email: true,
      month: true,
      number: true,
      range: true,
      search: true,
      tel: true,
      text: true,
      time: true,
      url: true,
      week: true,
      textarea: true,
      select: true,
      password: true,
    },
    maskInputFn: () => '***',
    blockSelector: BLOCK_SELECTOR,
    slimDOMOptions: 'all',
    sampling: { mousemove: 100, scroll: 150, input: 'last' },
    checkoutEveryNms: 20 * 60_000,
    recordCanvas: false,
    collectFonts: false,
    inlineStylesheet: true,
  };
}
